import React from 'react';
import {
  Page, Section, SectionTitleUnderlined, SkillPills, ExperienceList,
  LineList, prettyLink
} from './_shared';

/**
 * "Tech" template (key: compact-tech).
 *
 * For software engineers, designers, and technical roles. Sans-serif body
 * with monospace section headers for a developer-friendly feel. Compact
 * spacing so technical resumes (which tend to have lots of detail) fit on
 * one page. Teal accent for the hint of personality.
 */

const ACCENT = '#0e7490';      // cyan-700 — confident teal
const PILL_BG = '#ecfeff';     // cyan-50
const HEADER_BG = '#0e7490';

const CompactTech = ({ data }) => {
  const sansFont = "'Inter', 'Helvetica Neue', Arial, sans-serif";
  const monoFont = "'JetBrains Mono', 'SF Mono', 'Menlo', Consolas, monospace";

  return (
    <Page
      accent={ACCENT}
      fontFamily={sansFont}
      padding="0"
      style={{ lineHeight: 1.4 }}
    >
      <header style={{
        background: HEADER_BG,
        color: '#fff',
        padding: '0.6in 0.75in 0.5in'
      }}>
        <h1 style={{
          fontSize: '22pt',
          fontWeight: 700,
          margin: 0,
          letterSpacing: '-0.01em'
        }}>
          {data.fullName}
        </h1>
        {data.jobTitle && (
          <div style={{
            fontSize: '11pt',
            marginTop: '2px',
            opacity: 0.9,
            fontFamily: monoFont
          }}>
            {data.jobTitle}
          </div>
        )}
        <div style={{ marginTop: '8px', fontSize: '9.5pt', opacity: 0.95, lineHeight: 1.5 }}>
          {[
            data.email,
            data.phone,
            data.linkedin && prettyLink(data.linkedin),
            data.portfolio && prettyLink(data.portfolio)
          ].filter(Boolean).join('  ·  ')}
        </div>
      </header>

      <div style={{ padding: '0.6in 0.75in 0.75in' }}>
        {data.summary && (
          <Section>
            <SectionTitleUnderlined accent={ACCENT}>
              <span style={{ fontFamily: monoFont }}>{'// summary'}</span>
            </SectionTitleUnderlined>
            <p style={{ margin: 0 }}>{data.summary}</p>
          </Section>
        )}

        {data.skills && (
          <Section>
            <SectionTitleUnderlined accent={ACCENT}>
              <span style={{ fontFamily: monoFont }}>{'// skills'}</span>
            </SectionTitleUnderlined>
            <SkillPills skills={data.skills} accent={ACCENT} background={PILL_BG} />
          </Section>
        )}

        {data.experience && (
          <Section>
            <SectionTitleUnderlined accent={ACCENT}>
              <span style={{ fontFamily: monoFont }}>{'// experience'}</span>
            </SectionTitleUnderlined>
            <ExperienceList text={data.experience} accent={ACCENT} />
          </Section>
        )}

        {(data.education || data.certifications) && (
          <div style={{ display: 'grid', gridTemplateColumns: data.education && data.certifications ? '1fr 1fr' : '1fr', gap: '1rem' }}>
            {data.education && (
              <Section>
                <SectionTitleUnderlined accent={ACCENT}>
                  <span style={{ fontFamily: monoFont }}>{'// education'}</span>
                </SectionTitleUnderlined>
                <LineList text={data.education} />
              </Section>
            )}
            {data.certifications && (
              <Section>
                <SectionTitleUnderlined accent={ACCENT}>
                  <span style={{ fontFamily: monoFont }}>{'// certs'}</span>
                </SectionTitleUnderlined>
                <LineList text={data.certifications} />
              </Section>
            )}
          </div>
        )}

        {data.languages && (
          <Section>
            <SectionTitleUnderlined accent={ACCENT}>
              <span style={{ fontFamily: monoFont }}>{'// languages'}</span>
            </SectionTitleUnderlined>
            <LineList text={data.languages} />
          </Section>
        )}
      </div>
    </Page>
  );
};

export default CompactTech;
